import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as Yup from "yup";
import { getById, post } from '../_request';
import { useToast } from '../../modules/layout/components/ToastContext';
import { IAxiosGetResponse } from '../../interfaces/IUtilities';
import { AxiosResponse } from "axios";
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';

interface DependencyUploadInput {
    file: File;
    isTemplate: boolean;
}

interface LibraryFormValues {
    libraryPath: string;
    libraryFile: File | null;
    dependencies: DependencyUploadInput[];
}

const LibraryForm: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { serviceid, transactionid } = useParams<{ serviceid: string; transactionid: string }>();
    const { showToast } = useToast();

    const [service, setService] = useState('');
    const [transaction, setTransaction] = useState('');

    const fetchServiceName = async () => {
        try {
            let result = await getById("/api/services/", serviceid! + "/name");
            let respStatus = result?.status || result?.response.status;
            
            if (respStatus === 200) {
                setService(result.data as string);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else {
                showToast(t("CouldNotFetchServiceName", { id: serviceid }), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        }
    };

    const fetchClassName = async () => {
        try {
            let result = await getById("/api/transactionroutes/", transactionid + "/classname") as IAxiosGetResponse;
            if (result?.response.status === 200) {
                setTransaction(result.data as string);
            } else {
                showToast(t("CouldNotFetchClassName", { id: transactionid }), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        }
    };

    useEffect(() => {
        fetchServiceName();
        fetchClassName();
    }, []);


    const validationSchema = Yup.object({
        libraryPath: Yup.string()
            .required(t("LibraryPathRequired"))
            .test(
                "no-spaces",
                t("LibraryPathCannotContainSpaces"),
                (value) => {
                    if (!value) return true;
                    return !/\s/.test(value);
                }
        )
            .test(
                "no-special-chars",
                t("LibraryPathNoSpecialChars"),
                (value) => {
                    if (!value) return true;
                    return /^[A-Za-z0-9._-]+$/.test(value);
                }
            ),
        libraryFile: Yup.mixed<File>()
            .required(t("MainLibraryFileRequired"))
            .test("fileExists", t("MainLibraryFileRequired"), (value) => value instanceof File),
    });

    const initialValues: LibraryFormValues = {
        libraryPath: '',
        libraryFile: null,
        dependencies: [],
    };

    const handleSubmit = async (values: LibraryFormValues) => {
        if (!transactionid) {
            showToast(t('TransactionRouteIdMissing'), 'error');
            return;
        }

        const formData = new FormData();
        if (values.libraryFile) {
            formData.append('library', values.libraryFile);
        }
        formData.append('libraryPath', values.libraryPath);
        formData.append('transactionsId', transactionid);

        values.dependencies.forEach((dep, index) => {
            formData.append(`dependencies[${index}].Dependency`, dep.file);
            formData.append(`dependencies[${index}].IsTemplate`, String(dep.isTemplate));
        });

        try {
            let result = await post('/api/library/uploadfiles', formData, true) as IAxiosGetResponse;
            console.log(result);

            if (result.response.status === 200) {
                showToast(t('UploadSuccess', { defaultValue: 'Files uploaded successfully!' }), 'success');
                navigate(`/services/${serviceid}/transactions/${transactionid}/libraries/list`);
            } else if (result.response.status === 400) {
                showToast(t('BadRequest', { error: result.data?.message || 'Invalid request' }), 'error');
            } else if (result.response.status === 403) {
                navigate("/error/403");
            } else if (result.response.status === 401) {
                navigate("/auth/login");
            } else if (result.response.status === 500) {
                navigate("/error/500");
            } else if (result.response.status === 409) {
                showToast(
                    t('LibraryPathAlreadyExists', {
                        defaultValue: 'Library path already exists.'
                    }),
                    'error'
                );
            } else {
                showToast(t('StatusCode', {
                    code: result.response.status, defaultValue: `Status Code ${result.response.status }` }), 'error');
            }
        } catch (error: any) {
            showToast(
                t('ConnectionError', { error: error.message || error.toString(), defaultValue: `Connection ${error}` }),
                'error'
            );
        }
    };

    return (
        <div className="container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t('Home'), href: '/' },
                    { title: t('Services'), href: '/services/list' },
                    {
                        title: `${service}: ${t("TransactionRoutes")}`,
                        href: `/services/${serviceid}/transactions/list`,
                    },
                    {
                        title: `${transaction}: ${t("Libraries")}`,
                        href: `/services/${serviceid}/transactions/${transactionid}/libraries/list`,
                    },
                    { title: t('UploadLibrary') },
                ]}
            />

            <h3>{t('UploadLibrary')}</h3>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, setFieldValue }) => (
                    <Form>
                        {/* Main Library File */}
                        <div className="mb-3">
                            <label className="form-label">
                                {t('MainLibraryFile')} <span className="text-danger">*</span>
                            </label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={(e) => {
                                    if (e.currentTarget.files?.[0]) {
                                        setFieldValue("libraryFile", e.currentTarget.files[0]);
                                    }
                                }}
                            />
                            {touched.libraryFile && errors.libraryFile && (
                                <div className="text-danger">{errors.libraryFile}</div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                                {t('LibraryPath')} <span className="text-danger">*</span>
                            </label>
                            <Field name="libraryPath" className="form-control" />
                            {touched.libraryPath && errors.libraryPath && (
                                <div className="text-danger">{errors.libraryPath}</div>
                            )}
                        </div>

                        <div className="form-group mt-3">
                            <label className="form-label">{t('Dependencies')}</label>
                            <input
                                type="file"
                                className="form-control"
                                multiple
                                onChange={(e) => {
                                    const files = Array.from(e.currentTarget.files || []);
                                    setFieldValue("dependencies", [
                                        ...values.dependencies,
                                        ...files.map((f) => ({ file: f, isTemplate: false })),
                                    ]);
                                    e.currentTarget.value = "";
                                }}
                            />

                            {values.dependencies.length > 0 && (
                                <div className="mt-3">
                                    <label className="form-label">{t('SelectedFiles')}</label>
                                    <ul className="list-group">
                                        {values.dependencies.map((dep, index) => (
                                            <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                                <span className="text-truncate" style={{ maxWidth: "60%" }}>
                                                    {dep.file.name}
                                                </span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="form-check me-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={dep.isTemplate}
                                                            onChange={(e) => {
                                                                const newDeps = [...values.dependencies];
                                                                newDeps[index].isTemplate = e.target.checked;
                                                                setFieldValue("dependencies", newDeps);
                                                            }}
                                                        />
                                                        <label className="form-check-label">{t('Template')}</label>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => {
                                                            const newDeps = values.dependencies.filter((_, i) => i !== index);
                                                            setFieldValue("dependencies", newDeps);
                                                        }}
                                                    >
                                                        <Trash size={16} />
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <button type="submit" className="btn gg-btn-submit mt-4">
                            {t('Upload')}
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default LibraryForm;
